import { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { validateAgainstXss } from '@/utils/securityUtils';

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => boolean;
  preventXss?: boolean;
  trimValue?: boolean;
  email?: boolean;
  username?: boolean;
  name?: boolean;
  password?: boolean;
  url?: boolean;
}

interface ValidationError {
  message: string;
  type: 'required' | 'minLength' | 'maxLength' | 'pattern' | 'custom' | 'xss' | 'email' | 'username' | 'password' | 'url';
}

export const useInputValidation = (rules: ValidationRules = {}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<ValidationError | null>(null);
  const [isTouched, setIsTouched] = useState(false);

  const validate = useCallback((input: string): ValidationError | null => {
    const cleanValue = rules.trimValue ? input.trim() : input;

    if (rules.required && !cleanValue) {
      return { message: 'This field is required', type: 'required' };
    }

    if (rules.minLength && cleanValue.length < rules.minLength) {
      return { 
        message: `Must be at least ${rules.minLength} characters`, 
        type: 'minLength' 
      };
    }

    if (rules.maxLength && cleanValue.length > rules.maxLength) {
      return { 
        message: `Must not exceed ${rules.maxLength} characters`, 
        type: 'maxLength' 
      };
    }

    if (rules.email && cleanValue) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(cleanValue)) {
        return { message: 'Please enter a valid email address', type: 'email' };
      }
    }

    if (rules.username && cleanValue) {
      const usernamePattern = /^[a-zA-Z0-9_]{4,25}$/;
      if (!usernamePattern.test(cleanValue)) {
        return { 
          message: 'Username must be 4-25 characters and can only contain letters, numbers, and underscores', 
          type: 'username' 
        };
      }
    }

    if (rules.password && cleanValue) {
      const hasUpperCase = /[A-Z]/.test(cleanValue);
      const hasLowerCase = /[a-z]/.test(cleanValue);
      const hasNumbers = /\d/.test(cleanValue);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(cleanValue);
      
      if (!(hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar)) {
        return {
          message: 'Password must contain uppercase, lowercase, numbers, and special characters',
          type: 'password'
        };
      }
    }

    if (rules.url && cleanValue) {
      try {
        new URL(cleanValue);
      } catch {
        return { message: 'Please enter a valid URL', type: 'url' };
      }
    }

    if (rules.pattern && !rules.pattern.test(cleanValue)) {
      return { message: 'Invalid format', type: 'pattern' };
    }

    if (rules.preventXss !== false) {
      const cleanText = DOMPurify.sanitize(cleanValue);
      if (cleanText !== cleanValue) {
        return {
          message: 'Input contains potentially unsafe content',
          type: 'xss'
        };
      }
      
      const harmfulPatterns = [
        /<script/i,
        /javascript:/i,
        /data:/i,
        /vbscript:/i,
        /onclick/i,
        /onload/i,
        /onerror/i,
        /fromcharcode/i,
        /eval\(/i,
        /expression\(/i
      ];

      if (harmfulPatterns.some(pattern => pattern.test(cleanValue))) {
        return {
          message: 'Input contains potentially unsafe content',
          type: 'xss'
        };
      }
    }

    if (rules.custom && !rules.custom(cleanValue)) {
      return { message: 'Invalid input', type: 'custom' };
    }

    return null;
  }, [rules]);

  const handleChange = useCallback((input: string) => {
    const sanitizedValue = DOMPurify.sanitize(input);
    setValue(sanitizedValue);
    if (isTouched) {
      setError(validate(sanitizedValue));
    }
  }, [isTouched, validate]);

  const handleBlur = useCallback(() => {
    setIsTouched(true);
    setError(validate(value));
  }, [validate, value]);

  return {
    value,
    error,
    isTouched,
    handleChange,
    handleBlur,
    setValue,
    setError,
  };
};
